import { clsx } from "clsx";
import { sort } from "ramda";
import { forwardRef, type ForwardRefRenderFunction, memo, type ReactNode, useMemo, useRef } from "react";
import { type PressEvent, useButton, useHover } from "react-aria";
import { useDispatch } from "react-redux";
import { match } from "ts-pattern";
import {
  Checkbox,
  CheckboxGroup,
  ColoredLabel,
  HStack,
  Icons,
  Item,
  MenuOptionItem,
  MenuOptionItemIntent,
  MenuTriggerButton,
  Spacer,
  SymbolIcon,
  Tag,
  TagIntent,
  TimeAgo,
  TriggerButtonSize,
  VStack,
} from "widgets";
import { useDOMRef } from "widgets/hooks";
import { sortAlpha } from "widgets/utils";

import { getCssColor } from "@/app-utils/color.ts";
import { useRouteTo } from "@/app-utils/hooks/useRouteTo.ts";
import { ProjectColoredBadge } from "@/modules/projects/components/ProjectColoredWidgets.tsx";
import { OrganismTag } from "@/modules/saved-items/components/OrganismTag.tsx";
import { ProjectTag } from "@/modules/saved-items/components/ProjectTag.tsx";
import type {
  AnalystSessionSavedItemFragment,
  CoordinateListSavedItemFragment,
  GroupSavedItemFragment,
  ParentSavedItemFragment,
  RegionFlagSavedItemFragment,
  SavedItemFragment,
  SavedItemProjectFragment,
  SavedItemTagFragment,
  SavedItemUserFragment,
  ViewSavedItemFragment,
} from "@/modules/saved-items/graphql/saved-items.gql.ts";
import { RelativeUserLabel } from "@/modules/users/components/RelativeUserLabel.tsx";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";

import { pluralizeWord } from "../../../app-utils/string.js";
import type { CoordinateList, Maybe } from "../../../graphql.js";
import { downloadJnlpFile, type IJnlpDownloadInputData } from "../../../redux/modules/files.js";
import {
  getCoordListRoute,
  getFlagRoute,
  getGroupRoute,
  getParentRoute,
  getViewRoute,
  getViewTitle,
} from "../../../redux/modules/saved-view.js";
import { ENTITY_TYPE, EXT_APP_MODULES, FEATURE_DISPLAY_NAME, UNKNOWN_GENOME_ID } from "../../../settings/settings.ts";
import style from "../styles/Item.module.scss";

export interface ItemProps extends ItemTileProps {
  item: SavedItemFragment;
}

const ItemRender: ForwardRefRenderFunction<HTMLDivElement, ItemProps> = ({ item, ...props }, forwardedRef) => {
  const ref = useDOMRef(forwardedRef);

  return match(item)
    .with({ __typename: "RegionFlag" }, (flag: RegionFlagSavedItemFragment) => {
      return <RegionFlagItemTile ref={ref} flag={flag} {...props} />;
    })
    .with({ __typename: "CoordinateList" }, (coordinateList: CoordinateListSavedItemFragment) => {
      return <CoordinateListItemTile ref={ref} coordinateList={coordinateList} {...props} />;
    })
    .with({ __typename: "AnalystSession" }, (session: AnalystSessionSavedItemFragment) => {
      return <AnalystSessionItemTile ref={ref} session={session} {...props} />;
    })
    .with({ __typename: "Parent" }, (parent: ParentSavedItemFragment) => {
      return <ParentItemTile ref={ref} parent={parent} {...props} />;
    })
    .with({ __typename: "Group" }, (group: GroupSavedItemFragment) => {
      return <GroupItemTile ref={ref} group={group} {...props} />;
    })
    .with({ __typename: "View" }, (view: ViewSavedItemFragment) => {
      return <ViewItemTile ref={ref} view={view} {...props} />;
    })
    .otherwise((_savedItem) => {
      throw new Error(`Entity of type ${item.__typename} cannot be displayed as a saved list`);
    });
};

const RegionFlagItemTileRender: ForwardRefRenderFunction<
  HTMLDivElement,
  { flag: RegionFlagSavedItemFragment } & ItemTileProps
> = ({ flag, ...props }, ref) => {
  const navigate = useRouteTo();
  const sequence = flag.region.contig;
  const regionName =
    sequence.organism.id === UNKNOWN_GENOME_ID ? (
      <Tag intentColor={TagIntent.Secondary}>{sequence.sequenceId}</Tag>
    ) : (
      <OrganismTag organism={sequence.organism} />
    );

  return (
    <ItemTile
      ref={ref}
      id={flag.id}
      name={flag.name}
      description={flag.description}
      user={flag.lastModifier ?? flag.creator}
      dateCreated={flag.created}
      project={props.showProjectLabels ? flag.project : null}
      dateModified={flag.modified}
      hint={
        <Tag intentColor={TagIntent.Secondary} data-testid="region">
          {regionName}
        </Tag>
      }
      deletable={flag.deletable}
      typeLabel={flag.typeDisplayName}
      itemTags={flag.itemTags}
      private={!flag.shared}
      onPress={(event) => navigate(getFlagRoute(flag), event)}
      {...props}
    />
  );
};
const RegionFlagItemTile = forwardRef(RegionFlagItemTileRender);

const CoordinateListItemTileRender: ForwardRefRenderFunction<
  HTMLDivElement,
  { coordinateList: CoordinateListSavedItemFragment } & ItemTileProps
> = ({ coordinateList, ...props }, ref) => {
  const navigate = useRouteTo();

  return (
    <ItemTile
      ref={ref}
      id={coordinateList.id}
      icon={<img src={Icons.Tiles.GenomeViewer} width={30} height={30} />}
      itemCount={coordinateList.count}
      name={coordinateList.name}
      description={coordinateList.description}
      user={coordinateList.creator}
      dateCreated={coordinateList.created}
      project={props.showProjectLabels ? coordinateList.project : null}
      dateModified={coordinateList.modified}
      hint={coordinateList.organism != undefined ? <OrganismTag organism={coordinateList.organism} /> : undefined}
      deletable={coordinateList.deletable}
      typeLabel={FEATURE_DISPLAY_NAME[ENTITY_TYPE.COORDINATE]}
      itemTags={coordinateList.itemTags}
      onPress={
        coordinateList.organism != undefined
          ? (event) => navigate(getCoordListRoute(coordinateList as unknown as CoordinateList), event)
          : undefined
      }
      {...props}
    />
  );
};
const CoordinateListItemTile = forwardRef(CoordinateListItemTileRender);

const AnalystSessionItemTileRender: ForwardRefRenderFunction<
  HTMLDivElement,
  { session: AnalystSessionSavedItemFragment } & ItemTileProps
> = ({ session, ...props }, ref) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const jnlpDownloadPayload: IJnlpDownloadInputData = {
    moduleId: EXT_APP_MODULES.ANALYST.id,
    sessionId: session.id,
  };
  return (
    <ItemTile
      ref={ref}
      id={session.id}
      icon={<img src={Icons.Tiles.Analyst} width={30} height={30} />}
      name={session.name}
      description={session.description}
      user={session.creator}
      dateCreated={session.modified ?? session.created}
      project={props.showProjectLabels ? session.project : null}
      dateModified={session.modified}
      deletable={session.deletable}
      typeLabel={session.typeDisplayName}
      itemTags={session.itemTags}
      onPress={() => dispatch(downloadJnlpFile(jnlpDownloadPayload))}
      {...props}
    />
  );
};
const AnalystSessionItemTile = forwardRef(AnalystSessionItemTileRender);

const ParentItemTileRender: ForwardRefRenderFunction<
  HTMLDivElement,
  { parent: ParentSavedItemFragment } & ItemTileProps
> = ({ parent, ...props }, ref) => {
  const navigate = useRouteTo();

  return (
    <ItemTile
      ref={ref}
      id={parent.id}
      icon={<img src={Icons.Tiles.GenomeViewer} width={30} height={30} />}
      itemCount={parent.sampleCount}
      name={parent.name}
      description={parent.description}
      user={parent.creator}
      dateCreated={parent.created}
      project={props.showProjectLabels ? parent.project : null}
      dateModified={parent.modified}
      infoTags={[
        parent.status ? (
          <ColoredLabel
            color={parent.status.color ? getCssColor(parent.status.color) : undefined}
            label={parent.status.name ?? "not defined"}
          />
        ) : null,
      ]}
      deletable={parent.deletable}
      typeLabel={parent.typeDisplayName}
      itemTags={parent.itemTags}
      onPress={(event) => navigate(getParentRoute(parent), event)}
      {...props}
    />
  );
};
const ParentItemTile = forwardRef(ParentItemTileRender);

const GroupItemTileRender: ForwardRefRenderFunction<
  HTMLDivElement,
  { group: GroupSavedItemFragment } & ItemTileProps
> = ({ group, ...props }, ref) => {
  const navigate = useRouteTo();

  return (
    <ItemTile
      ref={ref}
      id={group.id}
      icon={<img src={Icons.Tiles.SavedGroup} width={30} height={30} />}
      itemCount={group.itemCount}
      name={group.name}
      description={group.description}
      user={group.creator}
      dateCreated={group.modified ?? group.created}
      project={props.showProjectLabels ? group.project : null}
      dateModified={group.modified}
      deletable={group.deletable}
      typeLabel={group.typeDisplayName}
      itemTags={group.itemTags}
      private={!group.shared}
      onPress={(event) => navigate(getGroupRoute(group), event)}
      {...props}
    />
  );
};
const GroupItemTile = forwardRef(GroupItemTileRender);

const ViewItemTileRender: ForwardRefRenderFunction<HTMLDivElement, { view: ViewSavedItemFragment } & ItemTileProps> = (
  { view, ...props },
  ref,
) => {
  const navigate = useRouteTo();
  const icon = match<keyof typeof VIEW, ReactNode>(view.viewType as keyof typeof VIEW)
    .with("GENOME_VIEW", () => <img src={Icons.Tiles.GenomeViewer} width={30} height={30} />)
    .with("SYNTENY_VIEW", () => <img src={Icons.Tiles.GenomeViewer} width={30} height={30} />)
    .with("DATA_VIEW", () => <img src={Icons.Tiles.AnalyticalDataViewer} width={30} height={30} />)
    .with("BLAST_JOB_VIEW", () => <img src={Icons.Tiles.Blast} width={30} height={30} />)
    .with("ENTITY_SUMMARY_VIEW", () => <img src={Icons.Tiles.Entity} width={30} height={30} />)
    .otherwise(() => <img src={Icons.Tiles.SavedItems} width={30} height={30} />);

  return (
    <ItemTile
      ref={ref}
      id={view.id}
      icon={icon}
      name={view.name}
      description={view.description}
      user={view.creator}
      dateCreated={view.modified ?? view.created}
      project={props.showProjectLabels ? view.project : null}
      dateModified={view.modified}
      deletable={view.deletable}
      // hint={<Tag intentColor={TagIntent.Secondary}>{getViewTitle(view)}</Tag>}
      infoTags={[
        <HStack spacing={1} key="type">
          <SymbolIcon icon="bookmark" size={16} />
          <span data-testid="type">{getViewTitle(view)}</span>
        </HStack>,
      ]}
      itemTags={view.itemTags}
      private={!view.shared}
      onPress={(event) => navigate(getViewRoute(view), event)}
      {...props}
    />
  );
};
const ViewItemTile = forwardRef(ViewItemTileRender);

// TODO: Delete and use ItemTile instead?
interface ListingItemData {
  icon?: ReactNode;
  onPress?: (e: PressEvent) => void;
  hint?: ReactNode;
  itemCount?: number | null;
  isSelected?: boolean;
  id: number;
  name: string | null;
  private?: boolean;
  description?: string | null;
  deletable?: boolean | null;
  typeLabel?: string | null;
  project?: Maybe<SavedItemProjectFragment>;
  dateCreated?: string;
  dateModified?: string;
  user: Maybe<SavedItemUserFragment>;
  infoTags?: ReactNode[];
  itemTags?: Maybe<SavedItemTagFragment[]>;
}

interface ItemTileProps {
  isSelected?: boolean;
  showProjectLabels: boolean;
  onItemSelectionChange: ((itemId: number, selected: boolean) => void) | undefined;
  onEditItem: () => void;
  onDeleteItem: () => void;
}

const ItemTileRender: ForwardRefRenderFunction<HTMLDivElement, ListingItemData & ItemTileProps> = (
  props,
  forwardedRef,
) => {
  const ref = useDOMRef(forwardedRef);
  const triggerRef = useRef<HTMLDivElement>(null);
  const { hoverProps, isHovered } = useHover({});
  const { buttonProps } = useButton({ onPress: props.onPress, elementType: "div" }, triggerRef);

  const tags = useMemo(() => {
    if (props.itemTags) {
      return sort((a, b) => sortAlpha(a.name, b.name), props.itemTags);
    }

    return null;
  }, [props.itemTags]);

  return (
    <HStack
      domProps={hoverProps}
      className={clsx(style.item, {
        [style.selected]: props.isSelected,
        [style.hovered]: isHovered,
      })}
      ref={ref}
    >
      {props.onItemSelectionChange && (
        <CheckboxGroup
          onChange={(selection) => {
            props.onItemSelectionChange?.(props.id, selection[0] === "selected");
          }}
          value={props.isSelected ? ["selected"] : []}
        >
          <Checkbox value="selected" />
        </CheckboxGroup>
      )}
      <div>{props.icon}</div>
      <VStack style={{ overflow: "hidden", width: "100%" }}>
        <VStack spacing={1}>
          <HStack>
            <div className={style.title} {...buttonProps} ref={triggerRef}>
              <HStack spacing={10}>
                <div data-testid="name">{props.name}</div>
                {props.itemCount && (
                  <Tag intentColor={TagIntent.Outlined} data-testid="count">
                    {props.itemCount} {pluralizeWord(props.typeLabel ?? "item", props.itemCount)}
                  </Tag>
                )}
                {props.hint}
              </HStack>
            </div>
            {props.private ? <SymbolIcon icon="lock" data-testid="private" /> : null}
          </HStack>
          <HStack spacing={10} className={style.info_tags}>
            {props.user && (
              <HStack spacing={1} key="owner">
                <SymbolIcon icon="person" size={16} />
                <RelativeUserLabel user={props.user} />
              </HStack>
            )}
            <HStack spacing={1} key="created">
              <SymbolIcon icon="calendar_today" size={16} />
              <TimeAgo dateTimeISO={props.dateModified ?? props.dateCreated} />
            </HStack>
            {props.infoTags}
          </HStack>
        </VStack>
        {props.description && <div data-testid="description">{props.description}</div>}
      </VStack>
      <Spacer />
      <HStack spacing={10} className={style.project_edit}>
        {tags?.map((tag) => <ProjectTag key={tag.id} projectTag={tag} />)}
        {props.showProjectLabels && props.project && <ProjectColoredBadge project={props.project} size="normal" />}
        {props.deletable ? (
          <MenuTriggerButton
            icon="more_vert"
            label=""
            data-testid="menu"
            size={TriggerButtonSize.Small}
            onAction={(key) => {
              switch (key) {
                case "saved-item-edit": {
                  props.onEditItem();
                  break;
                }
                case "saved-item-delete": {
                  props.onDeleteItem();
                  break;
                }
              }
            }}
          >
            <Item key="saved-item-edit">
              <MenuOptionItem id="saved-item-edit" icon="edit" intent={MenuOptionItemIntent.Default} title="Edit" />
            </Item>
            <Item key="saved-item-delete">
              <MenuOptionItem
                id="saved-item-delete"
                icon="delete"
                intent={MenuOptionItemIntent.Danger}
                title="Delete"
              />
            </Item>
          </MenuTriggerButton>
        ) : (
          <FakeTriggerButton />
        )}
      </HStack>
    </HStack>
  );
};

const FakeTriggerButton = () => <div style={{ width: 24, height: 20 }} />;

const ItemTile = forwardRef(ItemTileRender);
export const SavedItem = memo(forwardRef(ItemRender));
